/**
 * Upload Jobs Route Guard
 * Allows Graphic Designer to access only for re-upload (with jobId), not for new uploads
 */

import { Navigate, useLocation } from "react-router-dom";
import { type ReactNode } from "react";
import { useAuthStore } from "../stores/auth.store";
import { usePermissions } from "../hooks/usePermissions";

type UploadJobsRouteProps = {
  children: ReactNode;
};

/**
 * UploadJobsRoute Component
 * Allows access if:
 * - User can upload jobs (System Admin, Content Creator), OR
 * - User is Graphic Designer AND has jobId in state (re-upload scenario)
 */
export const UploadJobsRoute = ({ children }: UploadJobsRouteProps) => {
  const { isAuthenticated, isInitialized } = useAuthStore();
  const permissions = usePermissions();
  const location = useLocation();
  const reUploadJobId = (location.state as { jobId?: string })?.jobId;

  // Wait for auth initialization before checking authentication
  if (!isInitialized) {
    return null; // or a loading spinner
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Allow if user can upload jobs (System Admin, Content Creator)
  if (permissions.canUploadJobs) {
    return <>{children}</>;
  }

  // Allow Graphic Designer only if they have jobId (re-upload scenario)
  if (permissions.isGraphicDesigner && reUploadJobId) {
    return <>{children}</>;
  }

  // Block access for Graphic Designer trying to upload new jobs
  return <Navigate to="/" replace />;
};
